import { Link } from 'expo-router';
import { ScanLineIcon } from 'lucide-react-native';
import * as React from 'react';
import { View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { type EventCheckInEligibility } from '@sixam/shared/event-checkin-core';
import { fetchEventCheckInEligibility } from '@/lib/event-checkins';

type EventCheckInPanelProps = Readonly<{
  eventId: string;
  userId: string | null;
}>;

type EventCheckInSummaryProps = Readonly<{
  eligibility: EventCheckInEligibility;
  eventId?: string;
}>;

/**
 * The way into check-in from an event. It opens the full-screen station rather
 * than embedding a second, smaller scanner — there is one check-in screen in
 * the app, and every route reaches that same one.
 */
export function EventCheckInSummary({ eligibility, eventId }: EventCheckInSummaryProps) {
  if (!eligibility.canScan) {
    return null;
  }

  const button = (
    <Button>
      <ScanLineIcon color="white" size={18} />
      <Text>{eligibility.label}</Text>
    </Button>
  );

  return (
    <View className="gap-3 border-t border-border pt-4">
      <View className="gap-1">
        <Text variant="large">Event check-in</Text>
        <Text variant="muted">
          {eligibility.reason === 'checked_in_chain'
            ? 'You are checked in, so you can check in your friends.'
            : 'Scan runners into this event.'}
        </Text>
      </View>

      {eventId ? (
        <Link asChild href={{ params: { id: eventId }, pathname: '/admin/check-in/[id]' }}>
          {button}
        </Link>
      ) : (
        button
      )}
    </View>
  );
}

export function EventCheckInPanel({ eventId, userId }: EventCheckInPanelProps) {
  const [eligibility, setEligibility] = React.useState<EventCheckInEligibility | null>(null);

  React.useEffect(() => {
    let active = true;
    if (!userId) {
      return () => {
        active = false;
      };
    }

    fetchEventCheckInEligibility(eventId)
      .then((response) => {
        if (active) {
          setEligibility(response.eligibility);
        }
      })
      .catch(() => {
        // The panel is non-critical: if eligibility cannot load, it stays hidden.
      });

    return () => {
      active = false;
    };
  }, [eventId, userId]);

  if (!eligibility?.canScan) {
    return null;
  }

  return <EventCheckInSummary eligibility={eligibility} eventId={eventId} />;
}
